'use client';

import {
  Box,
  Link,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import Image from 'next/image';
import { useRef } from 'react';

import { ReactComponent as ArroRightBlack } from '@/app/_icons/Ck_arrow_right_black24px.svg';
import { ReactComponent as ChevropDown } from '@/app/_icons/CkChevronDown.svg';
import { ReactComponent as CkClose } from '@/app/_icons/CkClose_black.svg';
import { useOpenFloatingCTA } from '@/hooks/floating-cta.hook';
import { getDownloadLink, getImageLink } from '@/services/link.service';
import { ShoppingToolsMenu } from '@/services/menu/shoppingtools.service';

import styles from './MegaMenu.module.scss';

function ShoppingTools({
  isMobile = false,
  closeHamburger,
  shoppingToolsItems,
}: {
  isMobile?: boolean;
  closeHamburger?: () => void;
  shoppingToolsItems: ShoppingToolsMenu | null;
}) {
  const openLeadForm = useOpenFloatingCTA();
  const initRef = useRef<HTMLDivElement>(null);

  function openTestDrive() {
    openLeadForm('test-drive', 'TestDrive');
    if (closeHamburger) {
      closeHamburger();
    }
    openLeadForm('test-drive', 'TestDrive');
  }

  return (
    <Popover
      trigger="hover"
      placement="bottom"
      returnFocusOnClose={true}
      variant={!isMobile ? 'fullWidth' : 'mobileWidth'}
      initialFocusRef={initRef}
    >
      {({ onClose }) => (
        <>
          <PopoverTrigger>
            <Link
              role="link"
              className={styles.megaMenuLink}
            >
              Shopping Tools
              <ChevropDown className={styles.menuIcon} />
            </Link>
          </PopoverTrigger>
          <PopoverContent
            bg="white"
            className={styles.megaMenuWrapper}
            width="100%"
            border="0"
            borderRadius="0"
            top={11}
            bottom="0"
            left="0"
            right="0"
            boxShadow="none"
            zIndex="auto"
          >
            <PopoverBody p={0}>
              {!isMobile ? (
                <Box className={styles.megamenuHeader}>
                  <Box className={styles.megamenuFilter}>
                    <PopoverCloseButton
                      role="button"
                      className={styles.megamenuFilterClose}
                    >
                      <CkClose />
                    </PopoverCloseButton>
                  </Box>
                </Box>
              ) : null}
              <Box className={styles.megamenuBodyWrap}>
                <Box className={styles.megamenuBody}>
                  <SimpleGrid
                    columns={[1, 2, 3, 4]}
                    spacingX={['20px', '40px', '40px', '40px']}
                    spacingY={['20px', '40px', '40px', '40px']}
                    alignItems={'flex-start'}
                  >
                    {shoppingToolsItems?.items?.map((item, index) => {
                      return (
                        <Box
                          key={index}
                          className={styles.toolsCard}
                          textAlign={'center'}
                        >
                          <Image
                            src={
                              item?.icon ? getImageLink(item?.icon) ?? '' : ''
                            }
                            alt={item?.title}
                            className={styles.toolIcon}
                            width={40}
                            height={40}
                          />
                          {item?.show_book_test_drive ? (
                            <button
                              role="button"
                              onClick={openTestDrive}
                              className={styles.themeLink}
                            >
                              Book a Test Drive
                            </button>
                          ) : (
                            <Link
                              href={
                                item?.is_downloadable
                                  ? item?.document_id &&
                                    getDownloadLink(item?.document_id)
                                  : item?.url
                              }
                              role="link"
                              key={index}
                              target={
                                item?.is_downloadable ? '_blank' : '_self'
                              }
                              rel={
                                item?.is_downloadable
                                  ? 'noopener noreferrer'
                                  : ''
                              }
                              className={styles.themeLink}
                            >
                              {item?.title}
                              <ArroRightBlack className={styles.menuIcon} />
                            </Link>
                          )}
                          <Text className={styles.toolDescription}>
                            {item?.menu_description}
                          </Text>
                        </Box>
                      );
                    })}
                  </SimpleGrid>
                </Box>
              </Box>
              <Box onClick={onClose} ref={initRef} className={styles.overlay} />
            </PopoverBody>
          </PopoverContent>
        </>
      )}
    </Popover>
  );
}

export default ShoppingTools;
